"use client"

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { useState } from "react"

import { captureClientException } from "@/shared/lib/monitoring/capture-client"

function shouldSkipCapture(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  const message = (error as { message?: string }).message ?? ""
  const status = (error as { status?: number }).status
  const type = (error as { type?: string }).type
  const code = (error as { code?: string }).code
  if (status === 401 || status === 403) return true
  if (/401|403|unauth|not authenticated/i.test(message)) return true
  // rate_limit = user hit daily/hourly message cap — expected business logic, not a bug
  if (type === "rate_limit") return true
  // supabase_network_error = synthetic 503 that createReportingFetch substitutes
  // for a transient Supabase transport blip. That layer deliberately degrades to
  // a log (not an exception) to avoid minting false-positive issues; honor the
  // same decision here so the SDK-surfaced fallback isn't re-captured. Match on
  // the code only — a bare 503 status would also suppress genuine backend/gateway
  // outages. See src/shared/lib/supabase/reporting-fetch.ts.
  if (code === "supabase_network_error") return true
  // Next.js throws this when a server action receives a non-RSC response (expired session / stale build)
  if (message.includes("An unexpected response was received from the server")) return true
  return false
}

/**
 * React Query keys frequently contain user-controlled identifiers
 * (userId, characterId, search terms, etc.). Shipping the full key to
 * PostHog would leak those IDs into third-party telemetry. Return only
 * the first string element — by convention the feature namespace
 * (e.g. "characters", "conversations") — and drop the rest.
 */
function sanitizeKeyNamespace(key: unknown): string | undefined {
  if (!Array.isArray(key) || key.length === 0) return undefined
  const first = key[0]
  return typeof first === "string" ? first : undefined
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (shouldSkipCapture(error)) return
            captureClientException(error, {
              scope: "client-query",
              extra: {
                query_namespace: sanitizeKeyNamespace(query.queryKey),
              },
            })
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (shouldSkipCapture(error)) return
            captureClientException(error, {
              scope: "client-mutation",
              extra: {
                mutation_namespace: sanitizeKeyNamespace(
                  mutation.options.mutationKey
                ),
              },
            })
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            gcTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
            retry: 1,
          },
          mutations: {
            retry: 0,
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
